// Load portfolio types

export type SourceType = "solar" | "wind" | "ng" | "bess";
export type NGTurbineType = "ccgt" | "scgt" | "reciprocating";
export type PhaseType = "btm_only" | "interconnected";

export interface LoadPortfolioSummary {
  portfolio_id: string;
  portfolio_name: string;
  description: string | null;
  latitude: number;
  longitude: number;
  eia_ba_code: string | null;
  state: string | null;
  flat_load_mw: number | null;
  pue: number;
  current_phase: string;
  created_at: string | null;
  source_count: number;
  latest_run_status: string | null;
}

export interface LoadSource {
  source_id: string;
  source_name: string;
  source_type: SourceType;
  nameplate_capacity_mw: number;
  tech_type_id: string | null;
  sam_profile_flag: boolean | null;
  ng_turbine_type: string | null;
  ng_heat_rate_btu_kwh: number | null;
  ng_min_load_fraction: number | null;
  ng_has_scr: boolean | null;
  ng_scr_nox_reduction_pct: number | null;
  bess_capacity_mwh: number | null;
  bess_duration_hours: number | null;
  bess_round_trip_eff: number | null;
  bess_soc_min: number | null;
  bess_soc_max: number | null;
  online_year: number | null;
  offline_year: number | null;
  dispatch_priority: number | null;
  is_active: boolean;
  created_at: string | null;
}

export interface LoadRun {
  run_id: string;
  portfolio_id: string;
  phase: string;
  scenario_year: number | null;
  status: string;
  error_message: string | null;
  summary_json: LoadImpactSummary | null;
  dispatch_summary_json: DispatchSummary | null;
  s3_urls: Record<string, string> | null;
  created_at: string | null;
  completed_at: string | null;
}

export interface LoadPortfolioDetail {
  portfolio_id: string;
  portfolio_name: string;
  description: string | null;
  customer_id: string;
  latitude: number;
  longitude: number;
  eia_ba_code: string | null;
  project_nerc_code: string | null;
  timezone: string | null;
  state: string | null;
  county: string | null;
  flat_load_mw: number | null;
  load_profile_json: number[] | null;
  pue: number;
  current_phase: string;
  interconnect_year: number | null;
  created_at: string | null;
  updated_at: string | null;
  sources: LoadSource[];
  latest_run: LoadRun | null;
}

// Impact structures from the orchestrator
export interface CategoryBreakdown {
  DALYs: number;
  PDFs: number;
  USD: number;
}

export interface ImpactValues {
  DALYs: number;
  PDFs: number;
  USD: number;
  emissions?: Record<string, number>;
  impacts?: Record<string, CategoryBreakdown>;
  per_source?: Record<string, ImpactValues>;
}

export interface SCRComparison {
  enabled: boolean;
  nox_reduction_pct: number;
  without_scr: { ng_DALYs: number; ng_PDFs: number; ng_USD: number };
  with_scr: { ng_DALYs: number; ng_PDFs: number; ng_USD: number };
  scr_benefit: {
    DALYs_avoided: number;
    PDFs_avoided: number;
    USD_avoided: number;
    daly_pct_reduction: number;
    pdf_pct_reduction: number;
  };
  midpoint_reductions?: Record<
    string,
    { net_reduction: number; daly_delta: number; pdf_delta: number }
  >;
}

export interface GridSupplyMeta {
  marginal_method?: string;
  analysis_year?: number;
  grid_import_mwh?: number;
  per_fuel_mwh?: Record<string, number>;
  plant_rows?: unknown[];
}

export interface GridSupplyImpact extends ImpactValues {
  meta?: GridSupplyMeta;
}

export interface LoadImpactSummary {
  ng_impacts: ImpactValues;
  re_benefits: ImpactValues;
  re_infrastructure: ImpactValues & {
    per_source: Record<string, ImpactValues>;
  };
  // A1: residual grid-imported load attributed to the grid's marginal mix,
  // scored through the full ReCiPe/USEtox pipeline. Present when the
  // portfolio draws from the grid (interconnected, or btm residual).
  grid_supply?: GridSupplyImpact;
  net_impacts: ImpactValues & {
    interpretation: string;
  };
  // Attributional lens (average-grid mix): the same three lens-dependent blocks
  // as above, but the grid import is attributed to the grid's AVERAGE generation
  // mix instead of the marginal responders — the corporate/Scope-2-style
  // footprint vs. the consequential (marginal) number. Present only when the
  // model computed it; ng_impacts and re_infrastructure are lens-invariant so
  // they are not duplicated.
  grid_supply_attributional?: GridSupplyImpact;
  re_benefits_attributional?: ImpactValues;
  net_impacts_attributional?: ImpactValues & {
    interpretation: string;
  };
  scr_comparison?: SCRComparison;
}

export interface DailyDispatchPoint {
  day: number;
  load: number;
  re_direct: number;
  bess: number;
  ng: number;
  curtailed: number;
  unserved: number;
}

export interface DispatchSummary {
  total_load_mwh: number;
  total_re_gen_mwh: number;
  total_re_served_mwh: number;
  total_bess_discharged_mwh: number;
  total_ng_gen_mwh: number;
  total_curtailed_mwh: number;
  re_coverage_pct: number;
  ng_capacity_factor: number;
  bess_cycles: number;
  total_unserved_mwh?: number;
  max_unserved_mw?: number;
  hours_unserved?: number;
  daily_dispatch?: DailyDispatchPoint[];
}

// Form types for creating/editing
export interface LoadPortfolioCreateInput {
  customer_id?: string;
  portfolio_name: string;
  description?: string;
  latitude: number;
  longitude: number;
  eia_ba_code?: string;
  project_nerc_code?: string;
  state?: string;
  flat_load_mw?: number;
  load_profile_json?: number[];
  pue: number;
  current_phase: PhaseType;
}

export interface LoadSourceCreateInput {
  source_name: string;
  source_type: SourceType;
  nameplate_capacity_mw: number;
  tech_type_id?: string;
  sam_profile_flag?: boolean;
  ng_turbine_type?: NGTurbineType;
  ng_heat_rate_btu_kwh?: number;
  ng_min_load_fraction?: number;
  ng_has_scr?: boolean;
  ng_scr_nox_reduction_pct?: number;
  bess_capacity_mwh?: number;
  bess_duration_hours?: number;
  bess_round_trip_eff?: number;
  bess_soc_min?: number;
  bess_soc_max?: number;
  online_year?: number;
  offline_year?: number;
}

export type LoadSourceEditInput = Partial<LoadSourceCreateInput> & {
  is_active?: boolean;
};

export const SOURCE_TYPE_LABELS: Record<SourceType, string> = {
  solar: "Solar PV",
  wind: "Wind",
  ng: "Natural Gas",
  bess: "Battery Storage",
};

export const SOURCE_TYPE_COLORS: Record<SourceType, string> = {
  solar: "#f59e0b",
  wind: "#06b6d4",
  ng: "#ef4444",
  bess: "#8b5cf6",
};

export interface SourceGroup {
  key: string;
  baseName: string;
  sourceType: SourceType;
  capacityEach: number;
  sources: LoadSource[];
}
